// =====================================================
// 白板系统 - 服务层（增量存储版）
// 元素 → DB JSONB | 图片 → Supabase Storage 桶
// =====================================================

import { supabase } from '@/config/supabase';
import { getCachedFile, putCachedFile } from './whiteboard-cache';
import type {
  WhiteboardAccessState,
  WhiteboardCollaboratorMember,
  WhiteboardProject,
  CreateWhiteboardParams,
  UpdateWhiteboardParams,
} from '../types';

const TABLE = 'whiteboard_projects';
const STORAGE_BUCKET = 'whiteboards';

// =====================================================
// 项目 CRUD（Supabase DB）
// =====================================================

export async function fetchProjects(): Promise<WhiteboardProject[]> {
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  if (authError) throw authError;
  if (!user) return [];

  // “我的白板”只包含当前用户创建的项目。不能依赖 RLS 隐式过滤，
  // 因为已加入的协作者也拥有读取项目的权限。
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('created_by', user.id)
    .order('updated_at', { ascending: false });

  if (error) { console.error('Error fetching whiteboard projects:', error); throw error; }
  return data || [];
}

export async function fetchProjectById(
  id: string,
  shareToken?: string | null,
): Promise<WhiteboardProject | null> {
  const { data, error } = await supabase
    .from(TABLE).select('*').eq('id', id).maybeSingle();

  if (!error && data) {
    if (!shareToken) return data;

    // 创建者即使从带 token 的分享链接进入，也应优先使用已有 owner RLS，
    // 不应因为协作 RPC 尚未部署而打不开自己的白板。
    const { data: { session } } = await supabase.auth.getSession();
    const isOwner = data.created_by === session?.user.id;
    const tokenMatches = data.share_mode === 'edit' && data.share_token === shareToken;
    if (isOwner || tokenMatches) return data;
  }

  if (shareToken) return fetchProjectByShareToken(shareToken, id);

  // 已经通过分享链接加入过的成员，后续从“与我协作”打开时不再依赖 URL token。
  const joinedProject = await fetchJoinedProject(id);
  if (joinedProject) return joinedProject;

  if (error) {
    if (error.code === 'PGRST116') return null;
    console.error('Error fetching whiteboard project:', error);
    throw error;
  }
  return data;
}

export async function createProject(params?: CreateWhiteboardParams): Promise<WhiteboardProject> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id;

  const { data, error } = await supabase
    .from(TABLE)
    .insert({
      name: params?.name || '未命名白板',
      description: params?.description || null,
      storage_key: '', // 占位
      created_by: userId,
      updated_by: userId,
      canvas_data: { elements: [], appState: {} },
    })
    .select()
    .single();

  if (error) { console.error('Error creating whiteboard project:', error); throw error; }

  // 用 userId/projectId 作为 storage_key 前缀
  const key = `${userId}/${data.id}`;
  await supabase.from(TABLE).update({ storage_key: key }).eq('id', data.id);

  return { ...data, storage_key: key };
}

export async function updateProject(id: string, params: UpdateWhiteboardParams): Promise<WhiteboardProject> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id;

  const updateData: Record<string, unknown> = {
    updated_by: userId,
    updated_at: new Date().toISOString(),
  };

  if (params.name !== undefined) updateData.name = params.name;
  if (params.description !== undefined) updateData.description = params.description;
  if (params.thumbnail_url !== undefined) updateData.thumbnail_url = params.thumbnail_url;
  if (params.canvas_data !== undefined) updateData.canvas_data = params.canvas_data;

  const { data, error } = await supabase
    .from(TABLE).update(updateData).eq('id', id).select().single();

  if (error) { console.error('Error updating whiteboard project:', error); throw error; }
  return data;
}

export async function deleteProject(id: string): Promise<void> {
  const project = await fetchProjectById(id);

  const { error } = await supabase.from(TABLE).delete().eq('id', id);
  if (error) { console.error('Error deleting whiteboard project:', error); throw error; }

  // 删除 Storage 桶中的文件
  if (project?.storage_key) {
    try {
      const { data: files } = await supabase.storage
        .from(STORAGE_BUCKET)
        .list(project.storage_key);

      if (files?.length) {
        const paths = files.map((f: { name: string }) => `${project.storage_key}/${f.name}`);
        await supabase.storage.from(STORAGE_BUCKET).remove(paths);
      }
    } catch (e) {
      console.error('Error cleaning storage files:', e);
    }
  }
}

export async function duplicateProject(id: string): Promise<WhiteboardProject> {
  const original = await fetchProjectById(id);
  if (!original) throw new Error('Project not found');

  const newProject = await createProject({
    name: `${original.name} (副本)`,
    description: original.description,
  });

  // 复制画布数据
  if (original.canvas_data) {
    await updateProject(newProject.id, { canvas_data: original.canvas_data });
  }

  // 复制图片文件
  if (original.storage_key) {
    try {
      const { data: files } = await supabase.storage
        .from(STORAGE_BUCKET)
        .list(original.storage_key);

      if (files?.length) {
        await Promise.all(files.map(async (f: { name: string }) => {
          const { data } = await supabase.storage
            .from(STORAGE_BUCKET)
            .download(`${original.storage_key}/${f.name}`);
          if (data) {
            await supabase.storage
              .from(STORAGE_BUCKET)
              .upload(`${newProject.storage_key}/${f.name}`, data, { upsert: true });
          }
        }));
      }
    } catch (e) {
      console.error('Error duplicating storage files:', e);
    }
  }

  return newProject;
}

// =====================================================
// 画布元素数据（DB JSONB，快速）
// =====================================================

export interface CanvasElements {
  elements: any[];
  appState: Record<string, any>;
}

export interface SaveElementsOptions {
  /** 分享令牌仅用于首次加入；已有成员可为空。 */
  shareToken?: string | null;
  /** 当前本地已知的持久化版本；不一致时服务端拒绝覆盖。 */
  expectedVersion?: number | null;
}

export class WhiteboardVersionConflictError extends Error {
  constructor() {
    super('白板已被其他协作者更新，请先合并最新内容后重试保存');
    this.name = 'WhiteboardVersionConflictError';
  }
}

/**
 * 保存元素到 DB。优先使用带 token 校验和乐观锁的 RPC；所有者在迁移未执行时仍可
 * 走旧保存路径，避免部署切换瞬间让既有私有白板不可编辑。
 */
export async function saveElements(
  projectId: string,
  canvasData: CanvasElements,
  sessionId?: string,
  options: SaveElementsOptions = {},
): Promise<number | null> {
  const usesVersionedSave = options.shareToken !== undefined || options.expectedVersion !== undefined;
  if (usesVersionedSave) {
    const { data, error } = await supabase.rpc('save_whiteboard_canvas', {
      p_project_id: projectId,
      p_share_token: options.shareToken || null,
      p_canvas_data: canvasData,
      p_session_id: sessionId || null,
      p_expected_canvas_version: options.expectedVersion ?? null,
    });

    if (!error) return typeof data === 'number' ? data : Number(data);

    if (error.code === '40001' || error.message.includes('WHITEBOARD_VERSION_CONFLICT')) {
      throw new WhiteboardVersionConflictError();
    }

    // 部署切换期间兼容旧数据库；旧保存路径仍会附带 token 条件。
    const rpcMissing = error.code === 'PGRST202' || /save_whiteboard_canvas/i.test(error.message);
    if (!rpcMissing) {
      console.error('Error saving elements through collaboration RPC:', error);
      throw error;
    }
  }

  let updateQuery = supabase
    .from(TABLE)
    .update({
      canvas_data: canvasData,
      updated_at: new Date().toISOString(),
      last_save_session: sessionId || null,
    })
    .eq('id', projectId);

  if (options.shareToken) {
    updateQuery = updateQuery
      .eq('share_mode', 'edit')
      .eq('share_token', options.shareToken);
  }

  const { error } = await updateQuery;

  if (error) {
    console.error('Error saving elements:', error);
    throw error;
  }
  return null;
}

/** 加载元素（从 project.canvas_data） */
export function loadElements(project: WhiteboardProject): CanvasElements {
  const data = project.canvas_data as CanvasElements | null;
  return data || { elements: [], appState: {} };
}

// =====================================================
// 图片文件（Supabase Storage，增量）
// =====================================================

/** base64 DataURL → Uint8Array */
function dataURLToBytes(dataURL: string): { bytes: Uint8Array; mimeType: string } {
  const match = dataURL.match(/^data:([^;]+);base64,(.+)$/);
  if (!match) return { bytes: new Uint8Array(), mimeType: 'application/octet-stream' };

  const mimeType = match[1];
  const base64 = match[2];
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return { bytes, mimeType };
}

/** Blob → base64 DataURL */
async function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * 增量上传图片
 * 只上传 savedFileIds 中不存在的新文件
 * 返回新保存的 fileId 列表
 */
export async function saveFilesIncremental(
  storagePath: string,
  files: Record<string, any>,
  savedFileIds: Set<string>
): Promise<string[]> {
  const newEntries = Object.entries(files).filter(([id]) => !savedFileIds.has(id));
  if (newEntries.length === 0) return [];

  const saved: string[] = [];
  const failures: string[] = [];
  await Promise.all(
    newEntries.map(async ([id, fileData]) => {
      try {
        const { bytes, mimeType } = dataURLToBytes(fileData.dataURL);
        const { error } = await supabase.storage
          .from(STORAGE_BUCKET)
          .upload(`${storagePath}/${id}`, bytes, {
            contentType: mimeType,
            upsert: true,
          });

        if (error) {
          console.error(`Error uploading file ${id}:`, error);
          failures.push(id);
        } else {
          saved.push(id);
          void putCachedFile(`${storagePath}/${id}`, new Blob([bytes as BlobPart], { type: mimeType }));
        }
      } catch (e) {
        console.error(`Error processing file ${id}:`, e);
        failures.push(id);
      }
    })
  );

  if (failures.length > 0) {
    throw new Error(`白板图片上传失败：${failures.join(', ')}`);
  }

  return saved;
}

/** 单文件上传到 Storage（供导入功能使用） */
export async function uploadFileToStorage(
  storagePath: string,
  fileId: string,
  bytes: Uint8Array,
  mimeType: string
): Promise<void> {
  const { error } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(`${storagePath}/${fileId}`, bytes, {
      contentType: mimeType,
      upsert: true,
    });
  if (error) throw error;
}

/** 下载缩略图，返回 object URL（用户已认证，RLS 鉴权） */
export async function loadThumbnail(storagePath: string): Promise<string | null> {
  try {
    // 先检查文件是否存在，避免 400 错误
    const { data: files } = await supabase.storage
      .from(STORAGE_BUCKET)
      .list(storagePath, { search: 'thumbnail.png', limit: 1 });
    if (!files?.length) return null;

    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .download(`${storagePath}/thumbnail.png`);
    if (error || !data) return null;
    return URL.createObjectURL(data);
  } catch {
    return null;
  }
}

async function downloadFile(storagePath: string, id: string): Promise<any | null> {
  try {
    const cacheKey = `${storagePath}/${id}`;
    let blob = await getCachedFile(cacheKey);

    if (!blob) {
      const { data, error } = await supabase.storage
        .from(STORAGE_BUCKET)
        .download(cacheKey);

      if (error) {
        if (!error.message.includes('not found')) {
          console.error(`Error downloading file ${id}:`, error);
        }
        return null;
      }
      if (!data) return null;
      blob = data;
      void putCachedFile(cacheKey, data);
    }

    return {
      id,
      dataURL: await blobToDataURL(blob),
      mimeType: blob.type || 'image/png',
      created: Date.now(),
      lastRetrieved: Date.now(),
    };
  } catch (e) {
    console.error(`Error loading file ${id}:`, e);
    return null;
  }
}

/**
 * 按给定顺序（优先级从高到低）限并发下载图片，每张下载完立即回调，不等同批其他图片
 */
export async function loadFilesProgressive(
  storagePath: string,
  fileIds: string[],
  onFile: (file: any) => void,
  options: { concurrency?: number; shouldStop?: () => boolean } = {}
): Promise<void> {
  const { concurrency = 6, shouldStop } = options;
  const queue = [...new Set(fileIds)];
  let cursor = 0;

  const worker = async () => {
    while (cursor < queue.length) {
      if (shouldStop?.()) return;
      const id = queue[cursor++];
      const file = await downloadFile(storagePath, id);
      if (file && !shouldStop?.()) onFile(file);
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, worker));
}

/**
 * 按指定的 fileId 列表异步下载图片文件
 */
export async function loadFilesByIds(
  storagePath: string,
  fileIds: string[]
): Promise<Record<string, any>> {
  if (!fileIds || fileIds.length === 0) return {};

  const uniqueIds = [...new Set(fileIds)];
  const files: Record<string, any> = {};

  await Promise.all(
    uniqueIds.map(async (id) => {
      const file = await downloadFile(storagePath, id);
      if (file) files[id] = file;
    })
  );

  return files;
}

/**
 * 加载图片文件
 * 从 elements 中提取 image 元素的 fileId，批量下载
 */
export async function loadFiles(
  storagePath: string,
  elements: any[]
): Promise<Record<string, any>> {
  // 提取所有 image 元素的 fileId
  const fileIds = elements
    .filter((el: any) => el.type === 'image' && el.fileId && !el.isDeleted)
    .map((el: any) => el.fileId as string);

  return loadFilesByIds(storagePath, fileIds);
}

// =====================================================
// 素材库持久化（Supabase DB + 预装）
// =====================================================
const LIBRARY_TABLE = 'whiteboard_library';
const PRESET_LIBRARIES: { path: string; label: string }[] = [
  { path: '/libraries/flow-chart-symbols.excalidrawlib', label: '流程图' },
  { path: '/libraries/sticky-notes.excalidrawlib', label: '便利贴' },
  { path: '/libraries/bubbles.excalidrawlib', label: '气泡' },
  { path: '/libraries/handdrawn-signs.excalidrawlib', label: '标记' },
  { path: '/libraries/gadgets.excalidrawlib', label: '设备' },
  { path: '/libraries/gantt-diagram.excalidrawlib', label: '甘特图' },
  { path: '/libraries/decision-flow.excalidrawlib', label: '判断框' },
  { path: '/libraries/awesome-icons.excalidrawlib', label: '图标' },
  { path: '/libraries/information-architecture.excalidrawlib', label: '信息架构' },
  { path: '/libraries/forms.excalidrawlib', label: '表单' },
  { path: '/libraries/apple-devices-frames.excalidrawlib', label: 'Apple设备' },
];

/** 加载预装素材库（从 public 目录），统一格式并注入分类标签 */
async function loadPresetLibraries(): Promise<any[]> {
  const allItems: any[] = [];

  for (const { path, label } of PRESET_LIBRARIES) {
    try {
      const res = await fetch(path);
      if (!res.ok) continue;
      const lib = await res.json();

      // 收集原始 items
      const rawItems: any[] = [];
      if (lib.libraryItems && Array.isArray(lib.libraryItems)) {
        // v2 格式: { libraryItems: [{id, status, elements, name?, ...}] }
        rawItems.push(...lib.libraryItems);
      } else if (lib.library && Array.isArray(lib.library)) {
        // v1 格式: { library: [[element, ...], [element, ...]] }
        for (const entry of lib.library) {
          if (Array.isArray(entry)) {
            rawItems.push({
              id: crypto.randomUUID(),
              status: 'published',
              elements: entry,
              createdAt: Date.now(),
            });
          } else if (entry && typeof entry === 'object') {
            rawItems.push(entry);
          }
        }
      }

      // 为每个 item 注入分类标签前缀
      for (const item of rawItems) {
        const baseName = item.name || '';
        item.name = baseName ? `[${label}] ${baseName}` : `[${label}]`;
        if (!item.id) item.id = crypto.randomUUID();
        if (!item.status) item.status = 'published';
        if (!item.createdAt) item.createdAt = Date.now();
        allItems.push(item);
      }
    } catch {
      // 单个库加载失败不阻塞其他
    }
  }
  return allItems;
}

/** 加载用户素材库（含首次预装逻辑） */
export async function loadLibrary(): Promise<any[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data } = await supabase
    .from(LIBRARY_TABLE)
    .select('library_items')
    .eq('user_id', user.id)
    .single();

  // 已有记录 → 直接返回
  if (data?.library_items) return data.library_items;

  // 首次使用 → 加载预装素材并写入数据库
  const presetItems = await loadPresetLibraries();
  if (presetItems.length > 0) {
    await supabase.from(LIBRARY_TABLE).upsert({
      user_id: user.id,
      library_items: presetItems,
    }, { onConflict: 'user_id' });
  }
  return presetItems;
}

/** 保存用户素材库（upsert，每次变更都同步到数据库） */
export async function saveLibrary(libraryItems: any[]): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  await supabase.from(LIBRARY_TABLE).upsert({
    user_id: user.id,
    library_items: libraryItems,
  }, { onConflict: 'user_id' });
}

// =====================================================
// 协作功能
// =====================================================

/** 开启协作：生成 share_token，设 share_mode = 'edit' */
export async function enableCollaboration(projectId: string): Promise<string> {
  const token = crypto.randomUUID();
  const { error } = await supabase.rpc('configure_whiteboard_collaboration', {
    p_project_id: projectId,
    p_enabled: true,
    p_share_token: token,
  });

  if (error) {
    // 兼容迁移尚未执行的私有白板；协作安全策略在迁移执行后自动接管。
    if (error.code === 'PGRST202' || /configure_whiteboard_collaboration/i.test(error.message)) {
      const { error: legacyError } = await supabase
        .from(TABLE)
        .update({ share_token: token, share_mode: 'edit' })
        .eq('id', projectId);
      if (!legacyError) return token;
      throw legacyError;
    }
    console.error('Error enabling collaboration:', error);
    throw error;
  }
  return token;
}

/** 关闭协作：清 share_token，设 share_mode = 'private' */
export async function disableCollaboration(projectId: string): Promise<void> {
  const { error } = await supabase.rpc('configure_whiteboard_collaboration', {
    p_project_id: projectId,
    p_enabled: false,
    p_share_token: null,
  });

  if (error) {
    if (error.code === 'PGRST202' || /configure_whiteboard_collaboration/i.test(error.message)) {
      const { error: legacyError } = await supabase
        .from(TABLE)
        .update({ share_token: null, share_mode: 'private' })
        .eq('id', projectId);
      if (!legacyError) return;
      throw legacyError;
    }
    console.error('Error disabling collaboration:', error);
    throw error;
  }
}

/** 通过 projectId + share_token 获取项目，并由数据库登记当前协作者。 */
export async function fetchProjectByShareToken(
  token: string,
  projectId: string,
): Promise<WhiteboardProject | null> {
  const { data, error } = await supabase.rpc('get_whiteboard_by_share_token', {
    p_project_id: projectId,
    p_share_token: token,
  });

  if (error) {
    const rpcMissing = error.code === 'PGRST202' || /get_whiteboard_by_share_token/i.test(error.message);
    if (rpcMissing) {
      const { data: legacyData, error: legacyError } = await supabase
        .from(TABLE)
        .select('*')
        .eq('id', projectId)
        .eq('share_mode', 'edit')
        .eq('share_token', token)
        .maybeSingle();
      if (!legacyError) return legacyData;
      console.error('Error fetching project by legacy share token path:', legacyError);
      throw legacyError;
    }
    console.error('Error fetching project by share token:', error);
    throw error;
  }
  return Array.isArray(data) ? data[0] || null : null;
}

export async function fetchJoinedProject(projectId: string): Promise<WhiteboardProject | null> {
  const { data, error } = await supabase.rpc('get_joined_whiteboard', {
    p_project_id: projectId,
  });
  if (error) {
    console.error('Error fetching joined whiteboard:', error);
    throw error;
  }
  return Array.isArray(data) ? data[0] || null : null;
}

export async function fetchCollabProjects(): Promise<WhiteboardProject[]> {
  const { data, error } = await supabase.rpc('get_my_collaborative_whiteboards');
  if (error) {
    console.error('Error fetching collaborative whiteboards:', error);
    throw error;
  }
  return Array.isArray(data) ? data : [];
}

export async function setCollaborativeProjectPinned(projectId: string, pinned: boolean): Promise<boolean> {
  const { data, error } = await supabase.rpc('pin_whiteboard_project', {
    p_project_id: projectId,
    p_pinned: pinned,
  });
  if (error) throw error;
  return Boolean(data);
}

export async function getWhiteboardAccessState(projectId: string): Promise<WhiteboardAccessState> {
  const { data, error } = await supabase.rpc('get_whiteboard_access_state', {
    p_project_id: projectId,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return {
    hasAccess: Boolean(row?.has_access),
    isPinned: Boolean(row?.is_pinned),
    isOwner: Boolean(row?.is_owner),
    reason: (row?.access_reason || 'not_found') as WhiteboardAccessState['reason'],
  };
}

export async function listWhiteboardCollaborators(projectId: string): Promise<WhiteboardCollaboratorMember[]> {
  const { data, error } = await supabase.rpc('list_whiteboard_collaborators', {
    p_project_id: projectId,
  });
  if (error) throw error;
  return (data || []).map((row: any) => ({
    userId: row.user_id,
    name: row.name || '匿名',
    email: row.email || '',
    avatarUrl: row.avatar_url || undefined,
    joinedAt: row.joined_at,
    pinnedAt: row.pinned_at,
    isOwner: Boolean(row.is_owner),
  }));
}

export async function removeWhiteboardCollaborator(projectId: string, userId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('remove_whiteboard_collaborator', {
    p_project_id: projectId,
    p_user_id: userId,
  });
  if (error) throw error;
  return Boolean(data);
}

/** 获取用户信息（用于协作者显示名称和头像） */
export async function fetchUserProfile(userId: string): Promise<{ name: string; email: string; avatarUrl?: string }> {
  // 使用项目统一的用户视图 v_fs_users（与 user-store 一致）
  const { data: fsUser } = await supabase
    .from('v_fs_users')
    .select('name, email, avatar_url')
    .eq('id', userId)
    .single();

  if (fsUser?.name) {
    return {
      name: fsUser.name,
      email: fsUser.email || '',
      avatarUrl: fsUser.avatar_url || undefined,
    };
  }

  // 回退：用 email @ 前缀作为名字
  const email = fsUser?.email || '';
  return {
    name: email.split('@')[0] || '匿名',
    email,
    avatarUrl: fsUser?.avatar_url || undefined,
  };
}
